import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { extractApiErrorMessage, shouldReconnectQuickBooks } from '@api/errorHandling';
import {
  ERP_AUTH_PROVIDERS,
  getErpProviderLabel,
  isErpAuthProvider,
  redirectToErpAuthorization,
} from '@api/quickbooksAuth';
import { useNotificationStore } from '@store/notificationStore';
import { useAuthStore } from '@store/authStore';
import { BillingBlockedError } from '@/lib/BillingBlockedError';
import type { BillingBlockedBody } from '@/types/billing';

// Use relative /api path so nginx can proxy requests
// This works in both local dev (vite dev proxy) and Kubernetes (nginx proxy)
const API_URL = '/api';
const API_TIMEOUT = parseInt(import.meta.env.VITE_API_TIMEOUT || '30000');
const ERP_AUTH_STATUS_PATHS = ERP_AUTH_PROVIDERS.map((provider) => `/auth/${provider}/status`);
const ERP_CONNECTION_PATH = '/erp/connection';
const QUICKBOOKS_RECONNECT_COOLDOWN_MS = 5000;

let isQuickBooksReconnectPending = false;
let lastQuickBooksReconnectAt = 0;

const shouldTriggerQuickBooksReconnect = (error: AxiosError): boolean => {
  const requestUrl = error.config?.url ?? '';

  if (ERP_AUTH_STATUS_PATHS.some((path) => requestUrl.includes(path))) {
    return false;
  }

  if (!shouldReconnectQuickBooks(error)) {
    return false;
  }

  if (isQuickBooksReconnectPending) {
    return false;
  }

  return Date.now() - lastQuickBooksReconnectAt >= QUICKBOOKS_RECONNECT_COOLDOWN_MS;
};

const fetchActiveErpType = async (): Promise<string | null> => {
  try {
    const { data } = await axios.get<{ erp_type?: string }>(`${API_URL}${ERP_CONNECTION_PATH}`, {
      timeout: API_TIMEOUT,
      withCredentials: true,
    });
    return data.erp_type ?? null;
  } catch {
    return null;
  }
};

const triggerQuickBooksReconnect = async (notify: (message: string, type: 'warning' | 'error') => void) => {
  isQuickBooksReconnectPending = true;
  lastQuickBooksReconnectAt = Date.now();

  try {
    const erpType = await fetchActiveErpType();
    const providerLabel = getErpProviderLabel(erpType);
    if (!isErpAuthProvider(erpType)) {
      notify(`${providerLabel} connection requires attention. Open Integrations to check its connector.`, 'warning');
      return;
    }
    notify(`${providerLabel} connection expired. Redirecting to reconnect...`, 'warning');

    const redirected = await redirectToErpAuthorization(erpType);
    if (!redirected) {
      notify(`${providerLabel} reconnection is unavailable. Open Integrations and reconnect manually.`, 'error');
    }
  } catch {
    notify('Failed to start ERP reconnection. Open Integrations and reconnect manually.', 'error');
  } finally {
    isQuickBooksReconnectPending = false;
  }
};

// Stamp every outgoing request on the given instance with a fresh correlation
// ID so backend audit events can be tied back to the exact frontend action
// that triggered them, with no per-call-site wiring required. Shared between
// apiClient and publicApiClient — the latter's endpoints (e.g. accept-invite)
// still perform business-meaningful mutations despite being pre-auth.
const attachCorrelationIdInterceptor = (instance: AxiosInstance): void => {
  instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    config.headers.set('X-Correlation-ID', crypto.randomUUID());
    const authState = useAuthStore.getState();
    const activeTenantId = authState.activeTenantId ?? authState.currentUser?.active_tenant_id;
    const explicitTenantId = config.headers.get('X-Active-Tenant-ID');
    if (activeTenantId && !explicitTenantId) {
      config.headers.set('X-Active-Tenant-ID', activeTenantId);
    } else if (!activeTenantId && !explicitTenantId) {
      config.headers.delete('X-Active-Tenant-ID');
    }
    return config;
  });
};

const createAxiosInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: API_URL,
    timeout: API_TIMEOUT,
    headers: {
      'Content-Type': 'application/json',
    },
    withCredentials: true, // Send cookies with requests
  });

  attachCorrelationIdInterceptor(instance);

  // Response interceptor for error handling
  instance.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      const { addNotification } = useNotificationStore.getState();
      const { clearAuth } = useAuthStore.getState();
      const status = error.response?.status;
      const requestUrl = error.config?.url ?? '';
      const notify = (message: string, type: 'warning' | 'error') => {
        addNotification({ message, type });
      };

      if (shouldTriggerQuickBooksReconnect(error)) {
        void triggerQuickBooksReconnect(notify);
        return Promise.reject(error);
      }

      if (
        status === 402 &&
        (error.response?.data as BillingBlockedBody)?.error === 'billing_blocked'
      ) {
        const body = error.response?.data as BillingBlockedBody;
        try {
          addNotification({
            message: `Action blocked: ${String(body.reason).replace(/_/g, ' ')}`,
            type: 'warning',
            duration: 6000,
          });
        } catch {
          // notification store may not be available in some test environments — swallow
        }
        return Promise.reject(new BillingBlockedError(body));
      }

      if (status === 401) {
        // Unauthorized - clear auth state and redirect to login
        console.error('[apiClient] 401 Unauthorized from:', error.config?.url);
        clearAuth();
        addNotification({
          message: 'Session expired. Please log in again.',
          type: 'error',
        });
      } else if (status === 403) {
        const detail =
          (error.response?.data as Record<string, string>)?.detail ?? '';
        if (
          detail.includes('limit reached') ||
          detail.includes('Upgrade required') ||
          detail.includes('does not include')
        ) {
          addNotification({
            message: detail,
            type: 'warning',
            duration: 6000,
          });
        } else {
          addNotification({
            message: 'You do not have permission to perform this action.',
            type: 'error',
          });
        }
      } else if (status === 404) {
        if (requestUrl.includes(ERP_CONNECTION_PATH)) {
          return Promise.reject(error);
        }

        addNotification({
          message: extractApiErrorMessage(error, 'Resource not found'),
          type: 'error',
        });
      } else if (status === 400 || status === 422) {
        addNotification({
          message: extractApiErrorMessage(error, 'Invalid request'),
          type: 'error',
        });
      } else if (status && status >= 500) {
        addNotification({
          message: extractApiErrorMessage(error, 'Server error. Please try again later.'),
          type: 'error',
        });
      } else if (!error.response || error.code === 'ECONNABORTED') {
        addNotification({
          message: extractApiErrorMessage(error, 'Unable to reach the server. Please try again.'),
          type: 'error',
        });
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

export const apiClient = createAxiosInstance();

export const publicApiClient = axios.create({
  baseURL: API_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

attachCorrelationIdInterceptor(publicApiClient);
